import { useContext, useEffect, useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon, NewspaperIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from '../../../utils/axios';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';

type ArticleTab = 'all' | 'published' | 'draft';

interface ArticleForm {
  title: string;
  author: string;
  description: string;
  content: string;
  url: string;
  urlToImage: string;
}

const EMPTY_FORM: ArticleForm = {
  title: '', author: '', description: '', content: '', url: '', urlToImage: '',
};

/* ── ArticleModal ──────────────────────────────────────────────────── */
function ArticleModal({
  initial, token, onClose, onSaved,
}: {
  initial?: any;
  token: string;
  onClose: () => void;
  onSaved: (a: any, isEdit: boolean) => void;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState<ArticleForm>(
    isEdit
      ? {
          title:       initial.title       ?? '',
          author:      initial.author      ?? '',
          description: initial.description ?? '',
          content:     initial.content     ?? '',
          url:         initial.url         ?? '',
          urlToImage:  initial.urlToImage  ?? '',
        }
      : { ...EMPTY_FORM },
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k: keyof ArticleForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = isEdit
        ? await axios.put(`/news/${initial.id}`, form, { headers })
        : await axios.post('/news/create', form, { headers });
      onSaved(res.data?.data ?? res.data, isEdit);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to save article.');
    } finally {
      setSaving(false);
    }
  }

  const textField = (label: string, k: keyof ArticleForm, placeholder = '', required = false) => (
    <div>
      <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}{required && ' *'}</label>
      <input
        type="text"
        value={form[k]}
        onChange={set(k)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="w-full max-w-2xl rounded-2xl border border-white/8 overflow-hidden flex flex-col" style={{ background: '#071a0f', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/6 shrink-0 flex items-center justify-between">
          <div>
            <p className="text-green-400 text-[10px] uppercase tracking-widest mb-0.5">{isEdit ? 'Edit' : 'New'} Article</p>
            <h3 className="text-white/85 text-sm font-semibold">{isEdit ? `Editing: ${initial.title}` : 'Create New Article'}</h3>
          </div>
          <button onClick={onClose} className="text-white/25 hover:text-white/60 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">{textField('Title', 'title', 'Article headline…', true)}</div>
            {textField('Author / Source', 'author', 'e.g. KWS Communications')}
            {textField('Image URL', 'urlToImage', 'https://…/image.jpg')}
            <div className="col-span-2">{textField('Article URL', 'url', 'https://…/full-article')}</div>
          </div>

          <div className="mb-3">
            <label htmlFor="article-description" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">Description / Excerpt</label>
            <textarea
              id="article-description"
              value={form.description}
              onChange={set('description')}
              rows={2}
              placeholder="Short summary shown in the news feed..."
              className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 resize-none"
            />
          </div>

          <div>
            <label htmlFor="article-content" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">Full Content</label>
            <textarea
              id="article-content"
              value={form.content}
              onChange={set('content')}
              rows={6}
              placeholder="Full article body text..."
              className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 resize-none"
            />
          </div>

          {error && <p className="mt-3 text-red-400 text-xs">{error}</p>}
        </form>

        <div className="px-6 py-4 border-t border-white/6 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs text-white/50 hover:text-white/80 transition-colors">Cancel</button>
          <button
            onClick={submit as any}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Publish Article'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── DeleteConfirm ─────────────────────────────────────────────────── */
function DeleteConfirm({ title, onConfirm, onCancel, deleting }: { title: string; onConfirm: () => void; onCancel: () => void; deleting: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="w-full max-w-sm rounded-2xl border border-red-500/20 p-6" style={{ background: '#1a0505' }}>
        <p className="text-white/70 text-sm font-semibold mb-1">Delete Article</p>
        <p className="text-white/40 text-xs mb-5">Delete <span className="text-white/65">&ldquo;{title}&rdquo;</span>? This action cannot be undone.</p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-xs text-white/50 hover:text-white/80 transition-colors">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main View ─────────────────────────────────────────────────────── */
export default function ArticlesView() {
  const { state } = useContext(AuthContext);
  const token = state.userToken ?? '';

  const [articles,     setArticles]     = useState<any[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [tab,          setTab]          = useState<ArticleTab>('all');
  const [search,       setSearch]       = useState('');
  const [showModal,    setShowModal]    = useState(false);
  const [editTarget,   setEditTarget]   = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting,     setDeleting]     = useState(false);
  const [toggling,     setToggling]     = useState<Set<number>>(new Set());

  const [refreshing, setRefreshing] = useState(false);

  function fetchArticles(indicator: 'initial' | 'refresh' = 'initial') {
    if (indicator === 'initial') setLoading(true);
    else setRefreshing(true);
    // _t param busts browser cache on manual refresh
    axios
      .get('/news/all', { params: { _t: Date.now() } })
      .then((res) => { setArticles(res.data?.data ?? res.data ?? []); })
      .catch((_err) => { /* non-critical — user sees existing list */ })
      .finally(() => { setLoading(false); setRefreshing(false); });
  }

  useEffect(() => { fetchArticles('initial'); }, []);

  const filtered = articles.filter((a: any) => {
    const q = search.toLowerCase();
    const matchSearch = !q || a.title?.toLowerCase().includes(q) || a.author?.toLowerCase().includes(q);
    const status = a.published ? 'published' : 'draft';
    const matchTab = tab === 'all' || status === tab;
    return matchSearch && matchTab;
  });

  const counts = {
    all:       articles.length,
    published: articles.filter((a: any) => a.published).length,
    draft:     articles.filter((a: any) => !a.published).length,
  };

  const tabs: { id: ArticleTab; label: string }[] = [
    { id: 'all',       label: 'All' },
    { id: 'published', label: 'Published' },
    { id: 'draft',     label: 'Drafts' },
  ];

  function onSaved(a: any, isEdit: boolean) {
    if (isEdit) {
      setArticles((prev) => prev.map((x) => x.id === a.id ? a : x));
    } else {
      setArticles((prev) => [a, ...prev]);
    }
    setEditTarget(null);
    setShowModal(false);
  }

  async function togglePublish(a: any) {
    if (toggling.has(a.id)) return;
    setToggling((p) => new Set(Array.from(p).concat(a.id)));
    // Optimistic
    setArticles((prev) => prev.map((x) => x.id === a.id ? { ...x, published: !x.published } : x));
    try {
      const res = await axios.put(`/news/${a.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = res.data?.data ?? res.data;
      setArticles((prev) => prev.map((x) => x.id === a.id ? updated : x));
    } catch {
      // Revert on failure
      setArticles((prev) => prev.map((x) => x.id === a.id ? { ...x, published: a.published } : x));
    } finally {
      setToggling((p) => { const s = new Set(p); s.delete(a.id); return s; });
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/news/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArticles((prev) => prev.filter((x) => x.id !== deleteTarget.id));
    } catch { /* silent */ }
    finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
            Articles &amp; News
          </h2>
          <p className="text-white/30 text-xs mt-0.5">Manage published articles and conservation news</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
            <input
              className="bg-white/5 border border-white/10 text-white text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 w-48"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => fetchArticles('refresh')}
            disabled={refreshing}
            title="Refresh articles from backend"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-white/50 text-xs hover:text-white/80 hover:border-white/20 transition-all disabled:opacity-40"
          >
            <svg
              className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            onClick={() => { setEditTarget(null); setShowModal(true); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all"
          >
            <PlusIcon className="w-3.5 h-3.5" /> New Article
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-white/6">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-medium transition-all border-b-2 -mb-px ${
              tab === t.id
                ? 'text-green-400 border-green-500'
                : 'text-white/35 border-transparent hover:text-white/60'
            }`}
          >
            {t.label}
            <span className={`ml-1.5 text-[9px] tabular-nums ${tab === t.id ? 'text-green-400/70' : 'text-white/20'}`}>
              {counts[t.id]}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 rounded-full border-2 border-green-500/30 border-t-green-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <NewspaperIcon className="w-12 h-12 text-white/10" />
          <p className="text-white/25 text-sm">No articles found</p>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-full bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all"
          >
            Create First Article
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.15)' }}>
                <th className="text-left text-white/30 text-[9px] font-semibold uppercase tracking-wider py-3 pl-5 pr-5">Title</th>
                <th className="text-left text-white/30 text-[9px] font-semibold uppercase tracking-wider py-3 pr-5 hidden md:table-cell">Author</th>
                <th className="text-left text-white/30 text-[9px] font-semibold uppercase tracking-wider py-3 pr-5 hidden lg:table-cell">Date</th>
                <th className="text-left text-white/30 text-[9px] font-semibold uppercase tracking-wider py-3 pr-5">Status</th>
                <th className="text-right text-white/30 text-[9px] font-semibold uppercase tracking-wider py-3 pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a: any) => (
                <tr key={a.id} className="hover:bg-white/3 transition-colors group">
                  <td className="py-3 pl-5 pr-5 border-t border-white/5 max-w-xs">
                    <div className="flex items-start gap-2">
                      {a.urlToImage && (
                        <img
                          src={a.urlToImage}
                          alt=""
                          className="w-8 h-8 rounded object-cover shrink-0 opacity-70"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-white/85 text-xs truncate">{a.title || 'Untitled'}</p>
                        {a.description && <p className="text-white/30 text-[10px] mt-0.5 truncate">{a.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-5 border-t border-white/5 text-white/50 text-xs hidden md:table-cell">{a.author || '—'}</td>
                  <td className="py-3 pr-5 border-t border-white/5 text-white/35 text-xs hidden lg:table-cell whitespace-nowrap">
                    {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-3 pr-5 border-t border-white/5">
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${
                      a.published
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'
                        : 'bg-white/8 text-white/35 border-white/10'
                    }`}>
                      {a.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="py-3 pr-5 border-t border-white/5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setEditTarget(a); setShowModal(true); }}
                        className="text-sky-400 text-[10px] hover:text-sky-300 transition-colors"
                      >
                        Edit
                      </button>
                      <span className="text-white/15">|</span>
                      <button
                        onClick={() => togglePublish(a)}
                        disabled={toggling.has(a.id)}
                        className={`text-[10px] transition-colors disabled:opacity-40 ${a.published ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'}`}
                      >
                        {toggling.has(a.id) ? '…' : a.published ? 'Unpublish' : 'Publish'}
                      </button>
                      <span className="text-white/15">|</span>
                      <button
                        onClick={() => setDeleteTarget(a)}
                        className="text-red-400 text-[10px] hover:text-red-300 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New / Edit modal */}
      {showModal && (
        <ArticleModal
          initial={editTarget}
          token={token}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
          onSaved={onSaved}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <DeleteConfirm
          title={deleteTarget.title ?? 'this article'}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
